import React, {useContext, useEffect, useState} from 'react';
import {FormGroup, Label} from 'reactstrap';
import Dropzone from 'react-dropzone-uploader';
import AuthorizationContext from '../../../../_helper/Authorization';
import type {AuthorizationContextType} from '../../../../_helper/Authorization/AuthorizationTypes';
import MultimediaService from '../Services/Multimedia.service';
import MultimediaPreview from './MultimediaPreview';
import type {Multimedia, MultimediaRelation} from '../Types/MultimediaTypes';

interface MultimediaFieldProps {
    label?: string;
    name: string;
    register: any;
    setValue: any;
    errors?: any;
    options?: any;
    multimedia?: MultimediaRelation;
    disabled?: boolean;
    accept?: string;
    helperText?: string;
    uploadData?: Record<string, unknown>;
    onUploaded?: (multimedia: Multimedia) => void;
    onDeleted?: (multimedia: Multimedia | null) => void;
}

const MultimediaField: React.FC<MultimediaFieldProps> = ({
    label = 'Multimedia',
    name,
    register,
    setValue,
    errors = null,
    options = {},
    multimedia,
    disabled = false,
    accept = '*',
    helperText,
    uploadData = {},
    onUploaded,
    onDeleted,
}) => {
    const initialMultimedia = MultimediaService.normalizeMultimediaRelation(multimedia);
    const [selectedMultimedia, setSelectedMultimedia] = useState<Multimedia | null>(initialMultimedia);
    const [uploading, setUploading] = useState<boolean>(false);
    const [dropzoneKey, setDropzoneKey] = useState<number>(0);
    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const {errorHandler} = authContext || {};

    useEffect(() => {
        const normalizedMultimedia = MultimediaService.normalizeMultimediaRelation(multimedia);
        setSelectedMultimedia(normalizedMultimedia || null);
        setValue(name, normalizedMultimedia?.id || null, {
            shouldDirty: false,
            shouldValidate: false,
        });
    }, [multimedia, name, setValue]);

    const uploadFile = async (file?: File) => {
        if (!file) return;

        try {
            setUploading(true);
            const response = await MultimediaService.uploadMultimedia(file, uploadData);

            setSelectedMultimedia(response);
            setValue(name, response?.id || null, {
                shouldDirty: true,
                shouldValidate: true,
            });
            onUploaded?.(response);
        } catch (exception: any) {
            errorHandler(exception);
        } finally {
            setUploading(false);
        }
    };

    const getUploadParams = () => ({
        // autoUpload is disabled, so this URL is not used.
        url: 'https://httpbin.org/post',
    });

    const handleChangeStatus = ({file}: any, status: string) => {
        if (status === 'removed') return;
        if (disabled || uploading) return;
        if (status === 'ready') {
            uploadFile(file);
            setDropzoneKey((current) => current + 1);
        }
    };

    const handleDelete = async (currentMultimedia: Multimedia) => {
        try {
            await MultimediaService.deleteMultimedia(currentMultimedia.id);
            setSelectedMultimedia(null);
            setValue(name, null, {
                shouldDirty: true,
                shouldValidate: true,
            });
            onDeleted?.(currentMultimedia);
        } catch (exception: any) {
            errorHandler(exception);
        }
    };

    return (
        <FormGroup>
            <Label className={`col-form-label m-0 ${options?.required ? 'required' : ''}`}>{label}</Label>
            <input type='hidden' {...register(name, options)} />
            <div className='d-flex flex-column gap-3'>
                <Dropzone
                    key={dropzoneKey}
                    getUploadParams={getUploadParams}
                    onChangeStatus={handleChangeStatus}
                    maxFiles={1}
                    multiple={false}
                    canCancel={false}
                    autoUpload={false}
                    inputContent='Drop A File'
                    accept={accept}
                    styles={{
                        dropzone: {width: '100%', height: 150, opacity: disabled ? 0.6 : 1},
                        dropzoneActive: {borderColor: 'green'},
                    }}
                />
                {helperText ? <small className='text-muted'>{helperText}</small> : null}
                {uploading ? <small className='text-muted'>Uploading file...</small> : null}
                {selectedMultimedia ? (
                    <MultimediaPreview multimedia={selectedMultimedia} onDelete={handleDelete}/>
                ) : null}
            </div>
            {errors && (
                <span className='d-block invalid-feedback'>
                    {errors[name] && (errors[name].message ? errors[name].message : `Please provide a valid ${name}.`)}
                </span>
            )}
        </FormGroup>
    );
};

export default MultimediaField;


